/* eslint-disable no-param-reassign */
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { Tooltip } from 'components/overlay/Tooltip';

import { ButtonAskAI } from './buttonAskAI';
import { HeadingToolsOptions } from './headingToolsOptions';
import { ListToolsOptions } from './listToolsOptions';
import { LinkToolbarButton } from './linkTools';
import * as styled from '../styles';
import i18n from '../utils/i18n';

export const TOOLS_OPTIONS = {
  buttonAsk: { name: 'buttonAsk', icon: 'MagicWand' },
  heading: { name: 'heading' },
  bulletList: {
    name: 'bulletList',
    icon: 'ListDashes',
    message: i18n.toolBulletList,
  },
  orderedList: {
    name: 'orderedList',
    icon: 'ListNumbers',
    message: i18n.toolOrderedList,
  },
  bold: { name: 'bold', icon: 'TextBolder', message: i18n.toolBold },
  italic: { name: 'italic', icon: 'TextItalic', message: i18n.toolItalic },
  underline: {
    name: 'underline',
    icon: 'TextUnderline',
    message: i18n.toolUnderline,
  },
  strike: {
    name: 'strike',
    icon: 'TextStrikethrough',
    message: i18n.toolStrike,
  },
  highlight: {
    name: 'highlight',
    icon: 'PencilLine',
    message: i18n.toolHighlight,
  },
  lists: { name: 'lists' },
  link: { name: 'link' },
  insertTable: {
    name: 'insertTable',
    icon: 'Table',
    message: i18n.tableInsertTable,
  },
  horizontalRule: {
    name: 'horizontalRule',
    icon: 'Minus',
    message: i18n.toolHorizontalRule,
  },
};

export function TextToolsOptions({ editor, items, setAskAIOpen }) {
  const intl = useIntl();

  if (!editor) return false;

  return (
    <>
      {items.map((hashName) => {
        const { name, icon, message } = TOOLS_OPTIONS[hashName];
        const toggleName = `toggle${name.replace(
          /^.{1}/,
          name[0].toUpperCase()
        )}`;

        if (hashName === 'buttonAsk') {
          return (
            <ButtonAskAI
              icon={icon}
              onClick={() => setAskAIOpen(true)}
              editor={editor}
              floating
            />
          );
        }

        if (hashName === 'heading') {
          return <HeadingToolsOptions key={name} editor={editor} />;
        }

        if (hashName === 'lists') {
          return <ListToolsOptions key={name} editor={editor} />;
        }

        if (hashName === 'link') {
          return <LinkToolbarButton key={name} editor={editor} />;
        }

        if (hashName === 'insertTable') {
          return (
            <Tooltip
              key={name}
              color="dark"
              fixed
              position="S"
              content={intl.formatMessage(message)}
              padding={0}
            >
              <styled.MarkButton
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                    .run()
                }
              >
                <styled.Icon icon={icon} weight="bold" />
              </styled.MarkButton>
            </Tooltip>
          );
        }

        if (hashName === 'horizontalRule') {
          return (
            <Tooltip
              key={name}
              color="dark"
              fixed
              position="S"
              content={intl.formatMessage(message)}
              padding={0}
            >
              <styled.MarkButton
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
              >
                <styled.Icon icon={icon} weight="bold" />
              </styled.MarkButton>
            </Tooltip>
          );
        }

        return (
          <Tooltip
            key={name}
            color="dark"
            fixed
            position="S"
            content={intl.formatMessage(message)}
            padding={0}
          >
            <styled.MarkButton
              onClick={() => editor.chain().focus()[toggleName]().run()}
              active={editor.isActive(name)}
            >
              <styled.Icon icon={icon} weight="bold" />
            </styled.MarkButton>
          </Tooltip>
        );
      })}
    </>
  );
}

TextToolsOptions.defaultValues = {
  items: [],
};

TextToolsOptions.propTypes = {
  setAskAIOpen: PropTypes.func,
  editor: PropTypes.object,
  items: PropTypes.arrayOf(
    PropTypes.oneOf([
      'buttonAsk',
      'heading',
      'bulletList',
      'orderedList',
      'bold',
      'italic',
      'underline',
      'strike',
      'highlight',
      'lists',
      'link',
      'insertTable',
      'horizontalRule',
    ])
  ),
};
